// 通報モーダル（App Store Guideline 1.2 — flag objectionable content）
// 投稿・コメント・DM・ユーザー・トークルームで共通して使う。
// 送信すると通報者の画面からは即座に非表示になる（サーバー側 hidden_content）。
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const FALLBACK_REASONS = [
  { id: 'harassment', label: '嫌がらせ・いじめ' },
  { id: 'hate', label: 'ヘイト・差別的表現' },
  { id: 'sexual', label: '性的・わいせつな内容' },
  { id: 'violence', label: '暴力・危険な行為' },
  { id: 'spam', label: 'スパム・詐欺' },
  { id: 'child_safety', label: '未成年者に対する危害' },
  { id: 'self_harm', label: '自傷・自殺の助長' },
  { id: 'other', label: 'その他の不適切な内容' },
];

const TARGET_LABEL = {
  post: 'この投稿',
  comment: 'このコメント',
  message: 'このメッセージ',
  user: 'このユーザー',
  room: 'このトークルーム',
};

/**
 * @param {'post'|'comment'|'message'|'user'|'room'} targetType
 * @param {string} targetId
 * @param {string} [targetName] 通報対象の表示名（任意）
 * @param {() => void} onClose
 * @param {(info: {targetType: string, targetId: string}) => void} [onReported]
 *        通報成功時。呼び出し側で一覧から即座に取り除くために使う。
 */
export default function ReportModal({ targetType, targetId, targetName, onClose, onReported }) {
  const [reasons, setReasons] = useState(FALLBACK_REASONS);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get('/api/reports/reasons')
      .then(({ data }) => { if (alive && Array.isArray(data) && data.length) setReasons(data); })
      .catch(() => {}); // 取得できなくても組み込みの一覧で通報できる
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    if (!reason || busy) return;
    setBusy(true);
    setError('');
    try {
      await axios.post('/api/reports', {
        target_type: targetType,
        target_id: String(targetId),
        reason,
        detail: detail.trim(),
      });
      setDone(true);
      onReported?.({ targetType, targetId: String(targetId) });
    } catch (err) {
      setError(err?.response?.data?.error || '通報の送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  const what = TARGET_LABEL[targetType] || 'このコンテンツ';

  return (
    <div className="rp-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="通報">
      <style>{CSS}</style>
      <div className="rp-sheet" onClick={(e) => e.stopPropagation()}>
        <span className="rp-grabber" aria-hidden="true" />

        {done ? (
          <div className="rp-done">
            <span className="rp-done-badge" aria-hidden="true">✓</span>
            <h2 className="rp-done-title">通報を受け付けました</h2>
            <p className="rp-done-text">
              {what}はあなたの画面から非表示になりました。
              内容は<strong>24時間以内に確認</strong>し、規約違反が確認された場合はコンテンツを削除し、
              投稿したユーザーの利用を停止します。
            </p>
            <button data-control="primary" data-control-id="ReportModal-rp-primary" type="button" className="rp-primary" onClick={onClose}>閉じる</button>
          </div>
        ) : (
          <>
            <header className="rp-head">
              <h2 className="rp-title">{what}を通報</h2>
              <p className="rp-sub">
                {targetName ? <span className="rp-target">{targetName}</span> : null}
                理由を選んでください。通報した内容はすぐにあなたの画面から消えます。
              </p>
            </header>

            <div className="rp-reasons">
              {reasons.map((r) => (
                <button data-control="choice" data-control-id="ReportModal-rp-reason"
                  type="button"
                  key={r.id}
                  className={`rp-reason ${reason === r.id ? 'is-active' : ''}`}
                  onClick={() => setReason(r.id)}
                  aria-pressed={reason === r.id}
                >
                  <span className="rp-radio" aria-hidden="true" />
                  <span className="rp-reason-label">{r.label}</span>
                </button>
              ))}
            </div>

            <label className="rp-field">
              <span className="rp-field-label">詳細（任意）</span>
              <textarea
                className="rp-textarea"
                rows={3}
                maxLength={1000}
                placeholder="状況を教えてください（任意）"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
              />
            </label>

            {error ? <p className="rp-error">{error}</p> : null}

            <div className="rp-actions">
              <button data-control="secondary" data-control-id="ReportModal-rp-secondary" type="button" className="rp-secondary" onClick={onClose} disabled={busy}>
                キャンセル
              </button>
              <button data-control="primary" data-control-id="ReportModal-rp-primary"
                type="button"
                className="rp-primary"
                onClick={submit}
                disabled={!reason || busy}
              >
                {busy ? '送信中…' : '通報する'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CSS = `
.rp-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(15, 23, 30, 0.5);
  backdrop-filter: blur(2px);
  animation: rp-fade 0.2s ease both;
}
@keyframes rp-fade { from { opacity: 0; } to { opacity: 1; } }
.rp-sheet {
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 20px calc(20px + env(safe-area-inset-bottom));
  border-radius: 22px 22px 0 0;
  background: var(--surface);
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.18);
  animation: rp-up 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes rp-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
.rp-grabber {
  display: block;
  width: 38px;
  height: 4px;
  margin: 0 auto 14px;
  border-radius: 9999px;
  background: var(--border);
}
.rp-head { margin-bottom: 14px; }
.rp-title {
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 900;
  letter-spacing: -0.03em;
  color: var(--text);
}
.rp-sub {
  margin: 0;
  font-size: 13px;
  line-height: 1.65;
  color: var(--text-secondary);
}
.rp-target {
  display: block;
  font-weight: 800;
  color: var(--text);
  margin-bottom: 2px;
}
.rp-reasons { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.rp-reason {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid var(--border-light);
  border-radius: var(--r-input);
  background: var(--bg);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.18s, background 0.18s, transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.rp-reason:hover { transform: translateY(-2px); border-color: var(--primary-light); }
.rp-reason.is-active {
  border-color: var(--primary);
  background: var(--primary-ultra);
}
.rp-radio {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 9999px;
  border: 2px solid var(--border);
  background: var(--surface);
  transition: border-color 0.18s, box-shadow 0.18s;
}
.rp-reason.is-active .rp-radio {
  border-color: var(--primary);
  box-shadow: inset 0 0 0 4px var(--primary);
}
.rp-reason-label { font-size: 14px; font-weight: 700; color: var(--text); }
.rp-field { display: block; margin-bottom: 14px; }
.rp-field-label {
  display: block;
  margin-bottom: 6px;
  padding-left: 2px;
  font-size: 12px;
  font-weight: 800;
  color: var(--text-secondary);
}
.rp-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 11px 13px;
  border-radius: var(--r-input);
  border: 1.5px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 14px;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.rp-textarea:focus {
  border-color: var(--primary);
  background: var(--surface);
  box-shadow: 0 0 0 3px var(--primary-light);
}
.rp-error {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: var(--r-input);
  background: var(--error-light);
  color: var(--error);
  font-size: 13px;
  font-weight: 700;
  animation: shake 0.4s ease;
}
.rp-actions { display: flex; gap: 10px; }
.rp-secondary {
  flex: 1;
  padding: 14px 0;
  border-radius: var(--r-btn);
  border: 1.5px solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}
.rp-secondary:hover:not(:disabled) { transform: translateY(-2px); border-color: var(--text-muted); }
.rp-primary {
  flex: 1.4;
  padding: 14px 0;
  border-radius: var(--r-btn);
  border: none;
  background: var(--app-gradient);
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.35);
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s;
}
.rp-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(16, 185, 129, 0.45);
}
.rp-primary:disabled, .rp-secondary:disabled { opacity: 0.5; cursor: default; }
.rp-done { text-align: center; padding: 10px 4px 4px; }
.rp-done-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-bottom: 14px;
  border-radius: 22px;
  background: var(--app-gradient);
  color: #FFFFFF;
  font-size: 28px;
  font-weight: 900;
  box-shadow: 0 8px 22px rgba(16, 185, 129, 0.35);
  animation: bounceIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.rp-done-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: -0.03em;
  color: var(--text);
}
.rp-done-text {
  margin: 0 0 20px;
  font-size: 13.5px;
  line-height: 1.75;
  color: var(--text-secondary);
  text-align: left;
}
.rp-done .rp-primary { width: 100%; }
@media (prefers-reduced-motion: reduce) {
  .rp-overlay, .rp-sheet, .rp-done-badge { animation: none; }
  .rp-reason, .rp-primary, .rp-secondary { transition: none; }
}
`;
